import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Project } from '../types';
import ScrollToTop from '../components/ScrollToTop';

const Search: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/projects');
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError(i18n.language === 'en' ? 'Failed to fetch projects' : '获取项目失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [i18n.language]);

  const getLocalizedText = (zh: string, en: string | undefined) => {
    if (i18n.language === 'en' && en) {
      return en;
    }
    return zh;
  };

  const filteredProjects = projects.filter(project => {
    const searchTerm = query.toLowerCase();
    const title = getLocalizedText(project.title, project.title_en).toLowerCase();
    const description = getLocalizedText(project.description, project.description_en).toLowerCase();
    return title.includes(searchTerm) || description.includes(searchTerm);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-16">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900 mb-4"></div>
              <p className="text-gray-600 font-medium">{t('common.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[80px] min-h-screen bg-gray-50 font-PingFang">
      <ScrollToTop />
      <section className="py-10 bg-white">
        <div className="max-w-[2000px] mx-auto px-4 md:px-12 py-12">
          {/* 标题和视图切换 */}
          <div className="relative mb-24">
            <div className="flex justify-between items-center">
              <h2 className="text-3xl font-bold">
                {i18n.language === 'en' ? `Search Results: "${query}"` : `搜索结果："${query}"`}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="absolute -bottom-6 left-0 right-0 h-[1px] bg-gray-300" />
          </div>

          {/* 搜索结果 */}
          {error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-red-500 text-lg mb-4">{error}</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-gray-500 text-lg mb-4">
                {i18n.language === 'en' 
                  ? `No results found for "${query}"`
                  : `未找到与"${query}"相关的结果`}
              </p>
              <Link
                to="/projects"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {i18n.language === 'en' ? 'Browse All Projects' : '浏览所有项目'}
              </Link>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {filteredProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group block bg-white"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={i18n.language === 'en' ? project.title_en : project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {i18n.language === 'en' ? project.title_en : project.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {i18n.language === 'en' ? project.description_en : project.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProjects.map((project: Project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="group block py-5 border-b border-gray-100"
                >
                  <div className="flex justify-between items-center">
                    <div className="w-48">
                      <h3 className="text-base font-medium text-gray-900">
                        {i18n.language === 'en' ? project.title_en : project.title}
                      </h3>
                    </div>
                    <div className="flex-1 px-12">
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {i18n.language === 'en' ? project.description_en : project.description}
                      </p>
                    </div>
                    <div className="w-24 text-right">
                      <span className="text-sm text-gray-500 border-b border-gray-300 pb-0.5">
                        {i18n.language === 'en' ? 'View Details' : '查看详情'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Search; 